import { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { ScanLine, Package, MapPin, CheckCircle2, X } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function RackAssignment() {
  const [step, setStep] = useState('product'); // 'product' | 'rack' | 'done'
  const [productBarcode, setProductBarcode] = useState('');
  const [product, setProduct] = useState(null);
  const [rackCode, setRackCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [performedBy, setPerformedBy] = useState('');
  const productInputRef = useRef(null);
  const rackInputRef = useRef(null);

  useEffect(() => {
    if (step === 'product') productInputRef.current?.focus();
    if (step === 'rack') rackInputRef.current?.focus();
  }, [step]);

  const lookupProduct = async (e) => {
    e.preventDefault();
    if (!productBarcode.trim()) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/products/barcode/${productBarcode.trim()}`);
      setProduct(response.data);
      setStep('rack');
    } catch (error) {
      toast.error(error.response?.status === 404 ? 'Product not found' : 'Lookup failed');
      setProductBarcode('');
    } finally {
      setLoading(false);
    }
  };

  const assignRack = async (e) => {
    e.preventDefault();
    if (!rackCode.trim()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/products/assign-rack`, {
        product_barcode: product.barcode,
        rack_code: rackCode.trim(),
        performed_by: performedBy || null,
      });
      toast.success(`${product.sku} assigned to ${rackCode.toUpperCase()}`);
      setStep('done');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Assignment failed');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setProductBarcode('');
    setProduct(null);
    setRackCode('');
    setStep('product');
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6" data-testid="rack-assignment-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">Rack Assignment</h1>
        <p className="text-slate-600 mt-2">Scan a product then scan the rack to assign it</p>
      </div>

      {/* Progress indicator */}
      <div className="flex items-center gap-2 text-sm">
        <div className={`flex items-center gap-2 ${step === 'product' ? 'text-[#1680e5] font-semibold' : product ? 'text-green-600' : 'text-slate-400'}`}>
          {product ? <CheckCircle2 className="h-4 w-4" /> : <Package className="h-4 w-4" />}
          <span>1. Product</span>
        </div>
        <div className="flex-1 h-px bg-slate-200" />
        <div className={`flex items-center gap-2 ${step === 'rack' ? 'text-[#1680e5] font-semibold' : step === 'done' ? 'text-green-600' : 'text-slate-400'}`}>
          {step === 'done' ? <CheckCircle2 className="h-4 w-4" /> : <MapPin className="h-4 w-4" />}
          <span>2. Rack</span>
        </div>
      </div>

      {/* Optional operator name (persistent across scans) */}
      <div className="space-y-1">
        <Label className="text-xs">Operator (optional)</Label>
        <Input
          placeholder="Your name"
          value={performedBy}
          onChange={(e) => setPerformedBy(e.target.value)}
          data-testid="operator-name-input"
        />
      </div>

      {/* Step 1: Scan product */}
      {step === 'product' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ScanLine className="h-5 w-5 text-[#1680e5]" />
              Scan Product Barcode
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={lookupProduct}>
              <Input
                ref={productInputRef}
                type="text"
                inputMode="numeric"
                placeholder="Scan or type barcode..."
                value={productBarcode}
                onChange={(e) => setProductBarcode(e.target.value)}
                className="scan-input text-center text-2xl h-16 font-mono"
                autoFocus
                disabled={loading}
                data-testid="product-barcode-scan"
              />
            </form>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Scan rack */}
      {step === 'rack' && product && (
        <>
          <Card className="border-green-200 bg-green-50">
            <CardContent className="p-4 flex items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <CheckCircle2 className="h-5 w-5 text-green-600" />
                  <span className="text-sm font-semibold text-green-800">Product Identified</span>
                </div>
                <p className="font-semibold text-slate-900">{product.name}</p>
                <p className="text-sm text-slate-600">SKU: <code className="font-mono bg-white px-1.5 py-0.5 rounded">{product.sku}</code></p>
                {product.rack_location && (
                  <p className="text-xs text-slate-500 mt-1">
                    Currently at: <strong>{product.rack_location}</strong> (will be replaced)
                  </p>
                )}
              </div>
              <Button variant="ghost" size="icon" onClick={reset} data-testid="cancel-scan">
                <X className="h-4 w-4" />
              </Button>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ScanLine className="h-5 w-5 text-[#00a89c]" />
                Scan Rack Barcode
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={assignRack}>
                <Input
                  ref={rackInputRef}
                  type="text"
                  placeholder="Scan or type rack code..."
                  value={rackCode}
                  onChange={(e) => setRackCode(e.target.value)}
                  className="scan-input text-center text-2xl h-16 font-mono uppercase"
                  autoFocus
                  disabled={loading}
                  data-testid="rack-code-scan"
                />
              </form>
            </CardContent>
          </Card>
        </>
      )}

      {/* Step 3: Success */}
      {step === 'done' && product && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="p-6 text-center space-y-3">
            <CheckCircle2 className="h-16 w-16 text-green-600 mx-auto" />
            <h2 className="text-xl font-bold text-slate-900">Assigned Successfully</h2>
            <div className="text-slate-700">
              <p className="font-semibold">{product.name}</p>
              <p className="text-sm">SKU: <code className="font-mono bg-white px-1.5 py-0.5 rounded">{product.sku}</code></p>
              <p className="mt-2">Now at rack: <strong className="text-lg font-mono">{rackCode.toUpperCase()}</strong></p>
            </div>
            <Button
              onClick={reset}
              className="w-full h-12 text-base bg-[#1680e5] hover:bg-[#1680e5]/90 mt-4"
              data-testid="scan-next-button"
            >
              Scan Next Product
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
